import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 10000,
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('stockara_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ===== INVENTORY =====
export const inventoryAPI = {
  getMaterials: () => api.get('/inventory/materials'),
  createMaterial: (data) => api.post('/inventory/materials', data),
  updateMaterial: (id, data) => api.put(`/inventory/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/inventory/materials/${id}`),

  getProducts: () => api.get('/inventory/products'),
  createProduct: (data) => api.post('/inventory/products', data),
  updateProduct: (id, data) => api.put(`/inventory/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/inventory/products/${id}`),

  getMovements: (type) => api.get('/inventory/movements', { params: { type } }),
  createMovement: (data) => api.post('/inventory/movements', data),
  updateMovement: (id, data) => api.put(`/inventory/movements/${id}`, data),
  deleteMovement: (id) => api.delete(`/inventory/movements/${id}`),
  uploadStockInReceipt: (formData) => api.post('/inventory/movements/upload-receipt', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // foto nota wajib login (beda dari avatar/qris yg publik) - jadi tidak bisa dipakai langsung sbg <img src>, harus di-fetch sbg blob
  getStockInReceiptBlob: (photoUrl) => api.get(photoUrl.replace(/^\/api/, ''), { responseType: 'blob' }),

  getSuppliers: () => api.get('/inventory/suppliers'),
  createSupplier: (data) => api.post('/inventory/suppliers', data),
  updateSupplier: (id, data) => api.put(`/inventory/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/inventory/suppliers/${id}`),

  updateProductReorder: (id, data) => api.put(`/inventory/products/${id}/reorder-config`, data),
  updateMaterialReorder: (id, data) => api.put(`/inventory/materials/${id}/reorder-config`, data),
  getReorderSummary: () => api.get('/reorder/summary'),
  getReorderAlerts: (params = {}) => api.get('/reorder-alerts', { params }),
  runReorderCheck: () => api.post('/reorder/run-check'),
  createPOFromAlert: (id) => api.post(`/reorder-alerts/${id}/create-po`),
  dismissReorderAlert: (id) => api.post(`/reorder-alerts/${id}/dismiss`),

  getOpnameSessions: (params = {}) => api.get('/stock-opname/sessions', { params }),
  createOpnameSession: (data) => api.post('/stock-opname/sessions', data),
  getOpnameSession: (id) => api.get(`/stock-opname/sessions/${id}`),
  startOpnameSession: (id) => api.put(`/stock-opname/sessions/${id}/start`),
  getOpnameItems: (id, params = {}) => api.get(`/stock-opname/sessions/${id}/items`, { params }),
  updateOpnameItem: (sessionId, itemId, data) => api.put(`/stock-opname/sessions/${sessionId}/items/${itemId}`, data),
  completeOpnameSession: (id) => api.post(`/stock-opname/sessions/${id}/complete`),
  applyOpnameAdjustments: (id) => api.post(`/stock-opname/sessions/${id}/apply-adjustments`),
  deleteOpnameSession: (id) => api.delete(`/stock-opname/sessions/${id}`),
  exportOpnameSession: (id) => api.get(`/stock-opname/sessions/${id}/export`),
};

// ===== FINANCIAL =====
export const financialAPI = {
  getAccounts: (accountType) => api.get('/financial/accounts', { params: { account_type: accountType } }),
  createAccount: (data) => api.post('/financial/accounts', data),
  updateAccount: (id, data) => api.put(`/financial/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/financial/accounts/${id}`),
  getBalanceSheet: () => api.get('/financial/balance-sheet'),
  getProfitLoss: () => api.get('/financial/profit-loss'),
  getCashflow: () => api.get('/financial/cashflow'),

  getJournal: () => api.get('/financial/journal'),
  createJournal: (data) => api.post('/financial/journal', data),
  updateJournal: (id, data) => api.put(`/financial/journal/${id}`, data),
  deleteJournal: (id) => api.delete(`/financial/journal/${id}`),

  getReceivables: () => api.get('/financial/receivables'),
  createReceivable: (data) => api.post('/financial/receivables', data),
  updateReceivable: (id, status) => api.put(`/financial/receivables/${id}`, null, { params: { status } }),
  deleteReceivable: (id) => api.delete(`/financial/receivables/${id}`),

  getPayables: () => api.get('/financial/payables'),
  createPayable: (data) => api.post('/financial/payables', data),
  updatePayable: (id, status) => api.put(`/financial/payables/${id}`, null, { params: { status } }),
  deletePayable: (id) => api.delete(`/financial/payables/${id}`),

  createCashflow: (data) => api.post('/financial/cashflow', data),
  deleteCashflow: (id) => api.delete(`/financial/cashflow/${id}`),

  getBankAccounts: () => api.get('/financial/bank-accounts'),
  createBankAccount: (data) => api.post('/financial/bank-accounts', data),
  updateBankAccount: (id, data) => api.put(`/financial/bank-accounts/${id}`, data),
  deleteBankAccount: (id) => api.delete(`/financial/bank-accounts/${id}`),
  uploadQris: (formData) => api.post('/financial/upload-qris', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importBankStatements: (data) => api.post('/financial/bank-statements/import', data),
  getBankStatements: (params = {}) => api.get('/financial/bank-statements', { params }),
  matchBankStatement: (id, data) => api.post(`/financial/bank-statements/${id}/match`, data),
  unmatchBankStatement: (id) => api.post(`/financial/bank-statements/${id}/unmatch`),
  ignoreBankStatement: (id, data = {}) => api.post(`/financial/bank-statements/${id}/ignore`, data),
  getReconciliationSummary: () => api.get('/financial/reconciliation/summary'),
  autoMatchBankStatements: (data = {}) => api.post('/financial/reconciliation/auto-match', data),
  exportBankStatements: (params = {}) => api.get('/financial/bank-statements/export', { params }),

  getTaxConfig: () => api.get('/financial/tax-config'),
  updateTaxConfig: (data) => api.put('/financial/tax-config', data),
  createTaxTransaction: (data) => api.post('/financial/tax-transactions', data),
  getTaxTransactions: (params = {}) => api.get('/financial/tax-transactions', { params }),
  getTaxTransaction: (id) => api.get(`/financial/tax-transactions/${id}`),
  issueTaxTransaction: (id) => api.put(`/financial/tax-transactions/${id}/issue`),
  cancelTaxTransaction: (id, data) => api.put(`/financial/tax-transactions/${id}/cancel`, data),
  getTaxReport: (params = {}) => api.get('/financial/tax-report', { params }),
  generateTaxFromSales: (salesId, data = {}) => api.post(`/financial/tax-transactions/${salesId}/generate-from-sales`, data),
  getTaxPdf: (id) => api.get(`/financial/tax-transactions/${id}/pdf`, { responseType: 'blob' }),

  getExchangeRates: (params = {}) => api.get('/financial/exchange-rates', { params }),
  createExchangeRate: (data) => api.post('/financial/exchange-rates', data),
  updateExchangeRate: (id, data) => api.put(`/financial/exchange-rates/${id}`, data),
  deleteExchangeRate: (id) => api.delete(`/financial/exchange-rates/${id}`),
  getLatestExchangeRates: () => api.get('/financial/exchange-rates/latest'),
  convertCurrency: (params = {}) => api.get('/financial/exchange-rates/convert', { params }),
  getMultiCurrencyReport: (params = {}) => api.get('/financial/multicurrency-report', { params }),
};

// ===== CUSTOMERS =====
export const customersAPI = {
  getAll: () => api.get('/customers'),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// ===== SETTINGS =====
export const settingsAPI = {
  getCompany: () => api.get('/settings/company'),
  updateCompany: (data) => api.put('/settings/company', data),
};

// ===== WAREHOUSES =====
export const warehouseAPI = {
  list: () => api.get('/warehouses'),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
  stock: () => api.get('/warehouses/stock'),
};

// ===== STOCK TRANSFERS =====
export const transferAPI = {
  list: () => api.get('/stock-transfers'),
  create: (data) => api.post('/stock-transfers', data),
  delete: (id) => api.delete(`/stock-transfers/${id}`),
};

// ===== PURCHASE ORDERS =====
export const purchaseAPI = {
  list: () => api.get('/purchase-orders'),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  receive: (id) => api.put(`/purchase-orders/${id}/receive`),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

// ===== DELIVERY ORDERS =====
export const deliveryAPI = {
  list: () => api.get('/delivery-orders'),
  create: (data) => api.post('/delivery-orders', data),
  update: (id, data) => api.put(`/delivery-orders/${id}`, data),
  delete: (id) => api.delete(`/delivery-orders/${id}`),
  getPdf: (id) => api.get(`/delivery-orders/${id}/pdf`, { responseType: 'blob' }),
};

// ===== CHANNEL MANAGER =====
export const channelAPI = {
  catalog: () => api.get('/channels/catalog'),
  list: () => api.get('/channels'),
  create: (data) => api.post('/channels', data),
  update: (id, data) => api.put(`/channels/${id}`, data),
  delete: (id) => api.delete(`/channels/${id}`),
  activity: (id) => api.get(`/channels/${id}/activity`),
  sync: (id, action = 'routine_sync') => api.post(`/channels/${id}/sync`, { action }),
  importProducts: (id) => api.post(`/channels/${id}/sync`, { action: 'import_products' }),
  toggleActive: (id) => api.put(`/channels/${id}/toggle`),
  shopeeAuthorizeUrl: (id) => api.get(`/channels/${id}/shopee/authorize-url`),
};

// ===== IMPORT / TEMPLATES =====
export const importAPI = {
  downloadTemplate: async (kind) => {
    const res = await api.get(`/import/template/${kind}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template_${kind}.xlsx`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  upload: (kind, file, extra = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(extra || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) fd.append(key, value);
    });
    return api.post(`/import/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ===== NOTIFICATIONS =====
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// ===== REPORTS =====
export const reportsAPI = {
  stockCard: (sku) => api.get('/reports/stock-card', { params: { sku } }),
  agingReceivables: () => api.get('/reports/aging-receivables'),
  agingPayables: () => api.get('/reports/aging-payables'),
  salesByPeriod: (start, end) => api.get('/reports/sales-by-period', { params: { start_date: start, end_date: end } }),
};

// ===== PRODUCTION =====
export const productionAPI = {
  getOrders: () => api.get('/production/orders'),
  createOrder: (data) => api.post('/production/orders', data),
  updateOrder: (id, status) => api.put(`/production/orders/${id}`, null, { params: { status } }),
  startOrder: (id) => api.put(`/production/orders/${id}/start`),
  completeOrder: (id) => api.put(`/production/orders/${id}/complete`),
  deleteOrder: (id) => api.delete(`/production/orders/${id}`),

  getBoms: () => api.get('/production/bom'),
  getBom: (id) => api.get(`/production/bom/${id}`),
  createBom: (data) => api.post('/production/bom', data),
  updateBom: (id, data) => api.put(`/production/bom/${id}`, data),
  deleteBom: (id) => api.delete(`/production/bom/${id}`),

  getHpp: (params = {}) => api.get('/production/hpp', { params }),
  getHppSummary: () => api.get('/production/hpp/summary'),
  getHppDetail: (id) => api.get(`/production/hpp/${id}`),
  overrideHpp: (id, data) => api.post(`/production/hpp/${id}/override`, data),
  exportHpp: () => api.get('/production/hpp/export'),

  getMrpSummary: () => api.get('/production/mrp/summary'),
  getMrpPlans: (params = {}) => api.get('/production/mrp/plans', { params }),
  createMrpPlan: (data) => api.post('/production/mrp/plans', data),
  updateMrpPlan: (id, data) => api.put(`/production/mrp/plans/${id}`, data),
  deleteMrpPlan: (id) => api.delete(`/production/mrp/plans/${id}`),
  calculateMrpPlan: (id) => api.post(`/production/mrp/plans/${id}/calculate`),
  getMrpResult: (id) => api.get(`/production/mrp/plans/${id}/result`),
  generateMrpPo: (id) => api.post(`/production/mrp/plans/${id}/generate-po`),

  getQcInspections: (params = {}) => api.get('/production/qc/inspections', { params }),
  createQcInspection: (data) => api.post('/production/qc/inspections', data),
  getQcInspection: (id) => api.get(`/production/qc/inspections/${id}`),
  updateQcInspection: (id, data) => api.put(`/production/qc/inspections/${id}`, data),
  deleteQcInspection: (id) => api.delete(`/production/qc/inspections/${id}`),
  submitQcInspection: (id, data) => api.post(`/production/qc/inspections/${id}/submit`, data),
  getQcSummary: (params = {}) => api.get('/production/qc/summary', { params }),
  getQcReport: (params = {}) => api.get('/production/qc/report', { params }),
  getQcTemplates: () => api.get('/production/qc/templates'),
  createQcTemplate: (data) => api.post('/production/qc/templates', data),
  updateQcTemplate: (id, data) => api.put(`/production/qc/templates/${id}`, data),
  deleteQcTemplate: (id) => api.delete(`/production/qc/templates/${id}`),
  getQcTemplateForProduct: (id) => api.get(`/production/qc/templates/for-product/${id}`),
};

// ===== SALES =====
export const salesAPI = {
  getSales: (channel) => api.get('/sales', { params: { channel } }),
  createSale: (data) => api.post('/sales', data),
  deleteSale: (id) => api.delete(`/sales/${id}`),
  returnSale: (id, data) => api.post(`/sales/${id}/return`, data),
  getStats: () => api.get('/sales/stats'),
  getReceipt: (id) => api.get(`/sales/${id}/receipt`, { responseType: 'text' }),
  sendReceiptWa: (id, data) => api.post(`/sales/${id}/send-receipt-wa`, data),
};

// ===== DASHBOARD =====
export const dashboardAPI = {
  getStats: (month, year) => api.get('/dashboard/stats', { params: { month, year } }),
  getTopProducts: () => api.get('/dashboard/top-products'),
  getInsights: () => api.get('/dashboard/insights'),
  getBusinessUnits: (month, year) => api.get('/dashboard/business-units', { params: { month, year } }),
  getCashflow: () => api.get('/financial/cashflow'),
};

// ===== ONBOARDING =====
export const onboardingAPI = {
  getOnboardingProgress: () => api.get('/onboarding/progress'),
  completeOnboardingStep: (stepId) => api.post(`/onboarding/step/${stepId}/complete`),
  dismissOnboarding: () => api.post('/onboarding/dismiss'),
  checkOnboarding: () => api.get('/onboarding/check'),
};

// ===== SUPER ADMIN ANALYTICS =====
export const superAdminAPI = {
  getSuperAnalyticsOverview: () => api.get('/super/analytics/overview'),
  getSuperAnalyticsTenants: () => api.get('/super/analytics/tenants'),
  getTenantAnalytics: (tenantId) => api.get(`/super/tenants/${tenantId}/analytics`),
  getAtRiskTenants: () => api.get('/super/at-risk-tenants'),
  getMrrTrend: (months = 6) => api.get('/super/mrr-trend', { params: { months } }),
  sendSuperBroadcast: (data) => api.post('/super/broadcast', data),
  getSuperBroadcasts: () => api.get('/super/broadcasts'),
  exportPaymentsCSV: () => api.get('/super/payments/export-csv', { responseType: 'blob' }),
  getSuperPaymentInvoice: (txId) => api.get(`/super/payments/${txId}/invoice`, { responseType: 'blob' }),
  getTenantPayments: (tenantId) => api.get(`/super/tenants/${tenantId}/payments`),
  getTenantOnboarding: (tenantId) => api.get(`/super/tenants/${tenantId}/onboarding`),
  getTenantCustomPricing: (tenantId) => api.get(`/super/tenants/${tenantId}/custom-pricing`),
  updateTenantCustomPricing: (tenantId, data) => api.put(`/super/tenants/${tenantId}/custom-pricing`, data),
  getServerHealth: () => api.get('/super/server-health'),
  getAffiliateRevenueStats: () => api.get('/super/affiliate/revenue-stats'),
  getMetaAdsStats: (win) => api.get('/super/meta-ads-stats', { params: { window: win } }),
  refreshMetaAdsStats: () => api.post('/super/meta-ads-stats/refresh'),
  exportMetaAdsStats: (win) => api.get('/super/meta-ads-stats/export', { params: { window: win }, responseType: 'blob' }),
  getAdsAttribution: (win) => api.get('/super/ads-attribution', { params: { window: win } }),
  getDuitkuStatus: () => api.get('/super/duitku-status'),
  testDuitkuConnection: () => api.post('/super/duitku-test-connection'),
  getPaymentOrders: (params) => api.get('/super/payment-orders', { params }),
  exportPaymentOrders: (params) => api.get('/super/payment-orders/export', { params, responseType: 'blob' }),
  createAnnouncement: (data) => api.post('/super/announcements', data),
  listAnnouncements: () => api.get('/super/announcements'),
  deleteAnnouncement: (id) => api.delete(`/super/announcements/${id}`),
  toggleAnnouncement: (id) => api.patch(`/super/announcements/${id}/toggle`),
  getLoginHistory: (tenantId) => api.get(`/super/tenants/${tenantId}/login-history`),
  scheduleBroadcast: (data) => api.post('/super/broadcast/schedule', data),
  getScheduledBroadcasts: () => api.get('/super/broadcasts/scheduled'),
  cancelScheduledBroadcast: (id) => api.delete(`/super/broadcasts/scheduled/${id}`),
  getActiveAnnouncements: () => api.get('/announcements/active'),
  sendMarketingEmail: (data) => api.post('/super/marketing/send-email', data),
  sendMarketingWa: (data) => api.post('/super/marketing/send-wa', data),
  getMarketingCampaigns: () => api.get('/super/marketing/campaigns'),
  getProspects: () => api.get('/super/prospects'),
  getProspectMessages: (phone) => api.get(`/super/prospects/${phone}/messages`),
  replyProspect: (phone, data) => api.post(`/super/prospects/${phone}/reply`, data),
  toggleHandover: (phone, data) => api.post(`/super/prospects/${phone}/handover`, data),
  createTenant: (data) => api.post('/super/tenants/create', data),
  changeFirstPassword: (data) => api.post('/auth/change-first-password', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
};

// ===== INDUSTRY MODULE RECORDS =====
export const industryAPI = {
  list: (path) => api.get('/industry-records', { params: { path } }),
  create: (data) => api.post('/industry-records', data),
  update: (id, data) => api.put(`/industry-records/${id}`, data),
  delete: (id) => api.delete(`/industry-records/${id}`),
  downloadInvoice: async (id) => {
    const res = await api.get(`/industry-records/${id}/invoice`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice_${id}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ===== AI =====
export const aiAPI = {
  generateInsights: () => api.post('/ai/generate-insights'),
  getAiConversations: (params = {}) => api.get('/ai/conversations', { params }),
  getAiConversationDetail: (sender) => api.get(`/ai/conversations/${encodeURIComponent(sender)}`),
  getAiHandovers: (params = {}) => api.get('/ai/handovers', { params }),
  createAiHandover: (data) => api.post('/ai/handovers', data),
  resolveAiHandover: (handoverId, data = {}) => api.post(`/ai/handovers/${handoverId}/resolve`, data),
  getAiHandoverDetail: (handoverId) => api.get(`/ai/handovers/${handoverId}`),
  forecastStock: (weeks = 4) => api.post('/ai/forecast-stock', { weeks }),
  getWaBroadcasts: () => api.get('/broadcast/wa'),
  createWaBroadcast: (data) => api.post('/broadcast/wa', data),
  getWaBroadcastDetail: (id) => api.get(`/broadcast/wa/${id}`),
  deleteWaBroadcast: (id) => api.delete(`/broadcast/wa/${id}`),
  previewWaBroadcast: (params = {}) => api.get('/broadcast/wa/preview', { params }),
};

// ===== EXPORT =====
const downloadBlob = async (endpoint, filename) => {
  const res = await api.get(endpoint, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportAPI = {
  inventory: () => downloadBlob('/export/inventory?format=excel', 'stockara_inventory.xlsx'),
  financial: () => downloadBlob('/export/financial?format=excel', 'stockara_financial.xlsx'),
  sales: () => downloadBlob('/export/sales?format=excel', 'stockara_sales.xlsx'),
  inventoryPDF: () => downloadBlob('/export/inventory/pdf', 'stockara_inventory.pdf'),
  financialPDF: () => downloadBlob('/export/financial/pdf', 'stockara_financial.pdf'),
  salesPDF: () => downloadBlob('/export/sales/pdf', 'stockara_sales.pdf'),
  balanceSheetPDF: () => downloadBlob('/export/balance-sheet/pdf', 'stockara_neraca.pdf'),
  profitLossPDF: () => downloadBlob('/export/profit-loss/pdf', 'stockara_laba_rugi.pdf'),
};


// ===== FNB =====
export const fnbAPI = {
  // Menu
  getMenus: () => api.get('/fnb/menus'),
  createMenu: (data) => api.post('/fnb/menus', data),
  updateMenu: (id, data) => api.put(`/fnb/menus/${id}`, data),
  uploadMenuImage: (menuId, formData) => api.post(`/fnb/menus/${menuId}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteMenu: (id) => api.delete(`/fnb/menus/${id}`),
  // Tables
  getTables: () => api.get('/fnb/tables'),
  createTable: (data) => api.post('/fnb/tables', data),
  updateTable: (id, data) => api.put(`/fnb/tables/${id}`, data),
  deleteTable: (id) => api.delete(`/fnb/tables/${id}`),
  // Orders
  getOrders: (date) => api.get('/fnb/orders', { params: { date } }),
  createOrder: (data) => api.post('/fnb/orders', data),
  updateOrderStatus: (id, status) => api.put(`/fnb/orders/${id}/status`, { status }),
  setOrderPayments: (id, payments) => api.put(`/fnb/orders/${id}/payments`, { payments }),
  deleteOrder: (id) => api.delete(`/fnb/orders/${id}`),
  // Kitchen Display System
  getKdsOrders: () => api.get('/fnb/kds'),
  updateKdsItemCooking: (orderId, itemId) => api.put(`/fnb/kds/${orderId}/items/${itemId}/cooking`),
  updateKdsItemReady: (orderId, itemId) => api.put(`/fnb/kds/${orderId}/items/${itemId}/ready`),
  updateKdsAllReady: (orderId) => api.put(`/fnb/kds/${orderId}/all-ready`),
  // QR Order
  getQrConfig: () => api.get('/fnb/qr/config'),
  getQrTables: () => api.get('/fnb/qr/tables'),
  getPublicMenu: (tenantId) => api.get(`/public/fnb/${tenantId}/menu`),
  getPublicTable: (tenantId, tableId) => api.get(`/public/fnb/${tenantId}/tables/${tableId}`),
  submitPublicOrder: (tenantId, data) => api.post(`/public/fnb/${tenantId}/orders`, data),
  // Platform Orders
  getPlatformOrders: (params = {}) => api.get('/fnb/platform-orders', { params }),
  createPlatformOrder: (data) => api.post('/fnb/platform-orders', data),
  updatePlatformOrderStatus: (orderId, status) => api.put(`/fnb/platform-orders/${orderId}/status`, { status }),
  getPlatformOrdersSummary: (params = {}) => api.get('/fnb/platform-orders/summary', { params }),
  // Reports
  getPosSummary: (date) => api.get('/fnb/reports/pos-summary', { params: { date } }),
  // Table Layout
  getTableLayout: () => api.get('/fnb/table-layout'),
  setTableLayout: (data) => api.put('/fnb/table-layout', data),
  // Recipes
  getRecipes: () => api.get('/fnb/recipes'),
  createRecipe: (data) => api.post('/fnb/recipes', data),
  updateRecipe: (id, data) => api.put(`/fnb/recipes/${id}`, data),
  deleteRecipe: (id) => api.delete(`/fnb/recipes/${id}`),
  // Ingredients
  getIngredients: () => api.get('/fnb/ingredients'),
  createIngredient: (data) => api.post('/fnb/ingredients', data),
  updateIngredient: (id, data) => api.put(`/fnb/ingredients/${id}`, data),
  deleteIngredient: (id) => api.delete(`/fnb/ingredients/${id}`),
  // Reservations
  getReservations: () => api.get('/fnb/reservations'),
  createReservation: (data) => api.post('/fnb/reservations', data),
  updateReservation: (id, data) => api.put(`/fnb/reservations/${id}`, data),
  deleteReservation: (id) => api.delete(`/fnb/reservations/${id}`),
  // Waste
  getWaste: () => api.get('/fnb/waste'),
  createWaste: (data) => api.post('/fnb/waste', data),
  deleteWaste: (id) => api.delete(`/fnb/waste/${id}`),
  // Shifts
  getShifts: () => api.get('/fnb/shifts'),
  openShift: (data) => api.post('/fnb/shifts/open', data),
  closeShift: (id, data) => api.put(`/fnb/shifts/${id}/close`, data),
  // Promos
  getPromos: () => api.get('/fnb/promos'),
  createPromo: (data) => api.post('/fnb/promos', data),
  updatePromo: (id, data) => api.put(`/fnb/promos/${id}`, data),
  deletePromo: (id) => api.delete(`/fnb/promos/${id}`),
  // Modifier Groups
  getModifierGroups: () => api.get('/fnb/modifier-groups'),
  createModifierGroup: (data) => api.post('/fnb/modifier-groups', data),
  updateModifierGroup: (id, data) => api.put(`/fnb/modifier-groups/${id}`, data),
  deleteModifierGroup: (id) => api.delete(`/fnb/modifier-groups/${id}`),
  getMenuModifiers: (menuId) => api.get(`/fnb/menus/${menuId}/modifiers`),
};

// ===== FASHION =====
export const fashionAPI = {
  // Products (variant-based)
  getProducts: () => api.get('/fashion/products'),
  createProduct: (data) => api.post('/fashion/products', data),
  updateProduct: (id, data) => api.put(`/fashion/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/fashion/products/${id}`),
  // Variants
  getVariants: (productId) => api.get(`/fashion/products/${productId}/variants`),
  createVariant: (productId, data) => api.post(`/fashion/products/${productId}/variants`, data),
  updateVariant: (productId, variantId, data) => api.put(`/fashion/products/${productId}/variants/${variantId}`, data),
  deleteVariant: (productId, variantId) => api.delete(`/fashion/products/${productId}/variants/${variantId}`),
  // Sales / POS
  getSales: () => api.get('/fashion/sales'),
  createSale: (data) => api.post('/fashion/sales', data),
  deleteSale: (id) => api.delete(`/fashion/sales/${id}`),
  // BOM
  getBoms: () => api.get('/fashion/bom'),
  createBom: (data) => api.post('/fashion/bom', data),
  updateBom: (id, data) => api.put(`/fashion/bom/${id}`, data),
  deleteBom: (id) => api.delete(`/fashion/bom/${id}`),
  // Work Orders
  getWorkOrders: () => api.get('/fashion/work-orders'),
  createWorkOrder: (data) => api.post('/fashion/work-orders', data),
  updateWorkOrder: (id, data) => api.put(`/fashion/work-orders/${id}`, data),
  deleteWorkOrder: (id) => api.delete(`/fashion/work-orders/${id}`),
  // Cutting Orders
  getCuttingOrders: () => api.get('/fashion/cutting-orders'),
  createCuttingOrder: (data) => api.post('/fashion/cutting-orders', data),
  updateCuttingOrder: (id, data) => api.put(`/fashion/cutting-orders/${id}`, data),
  deleteCuttingOrder: (id) => api.delete(`/fashion/cutting-orders/${id}`),
  // Pre-Orders
  getPreOrders: () => api.get('/fashion/preorders'),
  createPreOrder: (data) => api.post('/fashion/preorders', data),
  updatePreOrder: (id, data) => api.put(`/fashion/preorders/${id}`, data),
  deletePreOrder: (id) => api.delete(`/fashion/preorders/${id}`),
  // Resellers
  getResellers: () => api.get('/fashion/resellers'),
  createReseller: (data) => api.post('/fashion/resellers', data),
  updateReseller: (id, data) => api.put(`/fashion/resellers/${id}`, data),
  deleteReseller: (id) => api.delete(`/fashion/resellers/${id}`),
  // Returns
  getReturns: () => api.get('/fashion/returns'),
  createReturn: (data) => api.post('/fashion/returns', data),
  deleteReturn: (id) => api.delete(`/fashion/returns/${id}`),
};

// ===== MANUFAKTUR =====
export const mfgAPI = {
  // Work Orders
  getWorkOrders: () => api.get('/mfg/work-orders'),
  createWorkOrder: (data) => api.post('/mfg/work-orders', data),
  updateWorkOrder: (id, data) => api.put(`/mfg/work-orders/${id}`, data),
  deleteWorkOrder: (id) => api.delete(`/mfg/work-orders/${id}`),
  startWorkOrder: (id) => api.put(`/mfg/work-orders/${id}/start`),
  completeWorkOrder: (id) => api.put(`/mfg/work-orders/${id}/complete`),
  // BOM Multi-Level
  getBoms: () => api.get('/mfg/bom'),
  createBom: (data) => api.post('/mfg/bom', data),
  updateBom: (id, data) => api.put(`/mfg/bom/${id}`, data),
  deleteBom: (id) => api.delete(`/mfg/bom/${id}`),
  // Quality Control
  getQcList: () => api.get('/mfg/qc'),
  createQc: (data) => api.post('/mfg/qc', data),
  updateQc: (id, data) => api.put(`/mfg/qc/${id}`, data),
  deleteQc: (id) => api.delete(`/mfg/qc/${id}`),
  // Assets / Machines
  getAssets: () => api.get('/mfg/assets'),
  createAsset: (data) => api.post('/mfg/assets', data),
  updateAsset: (id, data) => api.put(`/mfg/assets/${id}`, data),
  deleteAsset: (id) => api.delete(`/mfg/assets/${id}`),
  // Maintenance
  getMaintenance: () => api.get('/mfg/maintenance'),
  createMaintenance: (data) => api.post('/mfg/maintenance', data),
  updateMaintenance: (id, data) => api.put(`/mfg/maintenance/${id}`, data),
  deleteMaintenance: (id) => api.delete(`/mfg/maintenance/${id}`),
  // Production Plan
  getPlans: () => api.get('/mfg/plans'),
  createPlan: (data) => api.post('/mfg/plans', data),
  updatePlan: (id, data) => api.put(`/mfg/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/mfg/plans/${id}`),
  // Quotations B2B
  getQuotations: () => api.get('/mfg/quotations'),
  createQuotation: (data) => api.post('/mfg/quotations', data),
  updateQuotation: (id, data) => api.put(`/mfg/quotations/${id}`, data),
  deleteQuotation: (id) => api.delete(`/mfg/quotations/${id}`),
  // Sales Orders B2B
  getSalesOrders: () => api.get('/mfg/sales-orders'),
  createSalesOrder: (data) => api.post('/mfg/sales-orders', data),
  updateSalesOrder: (id, data) => api.put(`/mfg/sales-orders/${id}`, data),
  deleteSalesOrder: (id) => api.delete(`/mfg/sales-orders/${id}`),
  // Labor
  getLaborLogs: () => api.get('/mfg/labor'),
  createLaborLog: (data) => api.post('/mfg/labor', data),
  deleteLaborLog: (id) => api.delete(`/mfg/labor/${id}`),
  // Scrap & Reject
  getScrap: () => api.get('/mfg/scrap'),
  createScrap: (data) => api.post('/mfg/scrap', data),
  deleteScrap: (id) => api.delete(`/mfg/scrap/${id}`),
};

// ===== RETAIL =====
export const retailAPI = {
  // POS / Sales
  getSales: () => api.get('/retail/sales'),
  createSale: (data) => api.post('/retail/sales', data),
  deleteSale: (id) => api.delete(`/retail/sales/${id}`),
  // Customers / Loyalty
  getCustomers: () => api.get('/retail/customers'),
  createCustomer: (data) => api.post('/retail/customers', data),
  updateCustomer: (id, data) => api.put(`/retail/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/retail/customers/${id}`),
  addPoints: (id, points) => api.post(`/retail/customers/${id}/points`, { points }),
  // Products
  getProducts: () => api.get('/retail/products'),
  createProduct: (data) => api.post('/retail/products', data),
  updateProduct: (id, data) => api.put(`/retail/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/retail/products/${id}`),
  // Promotions
  getPromos: () => api.get('/retail/promos'),
  createPromo: (data) => api.post('/retail/promos', data),
  updatePromo: (id, data) => api.put(`/retail/promos/${id}`, data),
  deletePromo: (id) => api.delete(`/retail/promos/${id}`),
  // Stock Opname
  getOpnames: () => api.get('/retail/opname'),
  createOpname: (data) => api.post('/retail/opname', data),
  updateOpname: (id, data) => api.put(`/retail/opname/${id}`, data),
  deleteOpname: (id) => api.delete(`/retail/opname/${id}`),
  // Stores
  getStores: () => api.get('/retail/stores'),
  createStore: (data) => api.post('/retail/stores', data),
  updateStore: (id, data) => api.put(`/retail/stores/${id}`, data),
  deleteStore: (id) => api.delete(`/retail/stores/${id}`),
  // Online Orders
  getOnlineOrders: () => api.get('/retail/online-orders'),
  updateOnlineOrder: (id, data) => api.put(`/retail/online-orders/${id}`, data),
  // Cashier Sessions
  getSessions: () => api.get('/retail/sessions'),
  openSession: (data) => api.post('/retail/sessions/open', data),
  closeSession: (id, data) => api.put(`/retail/sessions/${id}/close`, data),
};

export default api;


// ===== AFFILIATE =====
// This is the NEW affiliateAPI section to replace the old one in api.js
// (lines 577-616)

const affiliateClient = axios.create({
  baseURL: API,
  timeout: 10000,
});
affiliateClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('stockara_affiliate_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const affiliateAPI = {
  // ── Affiliate-side ──
  register: (data) => affiliateClient.post('/affiliate/register', data),
  registerRequestOtp: (data) => affiliateClient.post('/affiliate/register/request-otp', data),
  registerVerifyOtp: (data) => affiliateClient.post('/affiliate/register/verify-otp', data),
  login: (data) => affiliateClient.post('/affiliate/login', data),
  logout: () => affiliateClient.post('/affiliate/logout'),
  me: () => affiliateClient.get('/affiliate/me'),
  updateMe: (data) => affiliateClient.put('/affiliate/me', data),
  changePassword: (data) => affiliateClient.post('/affiliate/me/change-password', data),
  dashboard: () => affiliateClient.get('/affiliate/dashboard'),
  referrals: (status, campaignId) => affiliateClient.get('/affiliate/referrals', { params: { status, campaign_id: campaignId } }),
  commissions: (periodMonth, status) => affiliateClient.get('/affiliate/commissions', { params: { period_month: periodMonth, status } }),
  commissionSummary: () => affiliateClient.get('/affiliate/commissions/summary'),
  payouts: () => affiliateClient.get('/affiliate/payouts'),
  requestPayout: (data) => affiliateClient.post('/affiliate/payouts/request', data),
  notifications: () => affiliateClient.get('/affiliate/notifications'),
  markNotificationsRead: () => affiliateClient.post('/affiliate/notifications/read-all'),
  campaigns: () => affiliateClient.get('/affiliate/campaigns'),
  createCampaign: (data) => affiliateClient.post('/affiliate/campaigns', data),
  updateCampaign: (id, data) => affiliateClient.put(`/affiliate/campaigns/${id}`, data),
  deleteCampaign: (id) => affiliateClient.delete(`/affiliate/campaigns/${id}`),
  contentKit: () => affiliateClient.get('/affiliate/content-kit'),
  leaderboard: () => affiliateClient.get('/affiliate/leaderboard'),
  publicProfile: (code) => affiliateClient.get(`/affiliate/p/${code}`),

  // ── Admin-side (uses main api with tenant token) ──
  adminStats: () => api.get('/admin/affiliates/stats'),
  adminList: (status, search) => api.get('/admin/affiliates', { params: { status, search } }),
  adminDetail: (id) => api.get(`/admin/affiliates/${id}`),
  adminUpdateStatus: (id, data) => api.put(`/admin/affiliates/${id}/status`, data),
  adminCommissions: (periodMonth, status) => api.get('/admin/affiliates/commissions', { params: { period_month: periodMonth, status } }),
  adminBulkApproveCommissions: (ids) => api.post('/admin/affiliates/commissions/bulk-approve', { commission_ids: ids }),
  adminUpdateCommission: (id, data) => api.put(`/admin/affiliates/commissions/${id}`, data),
  adminPayouts: (status) => api.get('/admin/affiliates/payouts', { params: { status } }),
  adminUpdatePayout: (id, data) => api.put(`/admin/affiliates/payouts/${id}`, data),
  adminUploadPayoutProof: (id, formData) => api.post(`/admin/affiliates/payouts/${id}/upload-proof`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminCalculateCommissions: () => api.post('/admin/affiliates/calculate-commissions'),
  adminApprove: (id) => api.post(`/admin/affiliates/${id}/approve`),
  adminEdit: (id, data) => api.put(`/admin/affiliates/${id}`, data),
  adminDelete: (id) => api.delete(`/admin/affiliates/${id}`),
  adminAdjustBalance: (id, data) => api.post(`/admin/affiliates/${id}/adjust-balance`, data),
  adminSettings: () => api.get('/admin/affiliates/settings'),
  adminUpdateSettings: (data) => api.put('/admin/affiliates/settings', data),
  adminPriorityStatus: () => api.get('/admin/affiliates/priority-program'),
  adminUpdatePriorityProgram: (data) => api.put('/admin/affiliates/priority-program', data),
  adminTogglePriority: (id, data) => api.post(`/admin/affiliates/${id}/priority-toggle`, data),
  adminReferralTrail: (status) => api.get('/admin/affiliates/referral-trail', { params: { status } }),
  adminExportCsv: () => api.get('/admin/affiliates/export-csv', { responseType: 'blob' }),
  adminPendingCount: () => api.get('/admin/affiliates/pending-count'),
  adminAnalytics: () => api.get('/admin/affiliates/analytics'),
  adminContentKit: () => api.get('/admin/affiliates/content-kit'),
  adminCreateContentKit: (data) => api.post('/admin/affiliates/content-kit', data),
  adminUploadContentKit: (formData) => api.post('/admin/affiliates/content-kit/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminUpdateContentKit: (id, data) => api.put(`/admin/affiliates/content-kit/${id}`, data),
  adminDeleteContentKit: (id) => api.delete(`/admin/affiliates/content-kit/${id}`),
  // KYC
  adminKycList: (status) => api.get('/admin/affiliates/kyc', { params: { status } }),
  adminKycReview: (id, data) => api.post(`/admin/affiliates/${id}/kyc/review`, data),
  adminKycPendingCount: () => api.get('/admin/affiliates/kyc/pending-count'),
  // Affiliate KYC (via affiliateClient)
  kycUpload: (formData) => affiliateClient.post('/affiliate/kyc/upload', formData, { timeout: 120000 }),
  kycStatus: () => affiliateClient.get('/affiliate/kyc/status'),
  uploadProfilePhoto: (formData) => affiliateClient.post('/affiliate/me/profile-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminUploadAffiliatePhoto: (id, formData) => api.post(`/admin/affiliates/${id}/profile-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Bank change OTP
  requestBankOtp: () => affiliateClient.post('/affiliate/bank/request-otp'),
  verifyBankOtp: (data) => affiliateClient.post('/affiliate/bank/verify-otp', data),
};

// ===== PAYROLL =====
export const payrollAPI = {
  getEmployees: () => api.get('/employees'),
  createEmployee: (data) => api.post('/employees', data),
  updateEmployee: (id, data) => api.put(`/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/employees/${id}`),
  getPayrollRuns: () => api.get('/payroll/runs'),
  createPayrollRun: (data) => api.post('/payroll/runs', data),
  getPayrollRun: (id) => api.get(`/payroll/runs/${id}`),
  approvePayrollRun: (id) => api.put(`/payroll/runs/${id}/approve`),
  deletePayrollRun: (id) => api.delete(`/payroll/runs/${id}`),
  downloadSlipPDF: async (runId, employeeId, employeeName) => {
    const res = await api.get(`/payroll/runs/${runId}/slip/${employeeId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `slip_gaji_${employeeName}_${runId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ===== LOYALTY =====
export const loyaltyAPI = {
  getConfig: () => api.get('/loyalty/config'),
  saveConfig: (data) => api.put('/loyalty/config', data),
  getCustomers: () => api.get('/loyalty/customers'),
  getCustomerPoints: (id) => api.get(`/loyalty/customer/${id}`),
  redeemPoints: (data) => api.post('/loyalty/redeem', data),
  getCustomerHistory: (id) => api.get(`/loyalty/customer/${id}/history`),
};

// ===== KNOWLEDGE BASE =====
export const knowledgeBaseAPI = {
  getDocs: () => api.get('/kb/documents'),
  createDoc: (data) => api.post('/kb/documents', data),
  deleteDoc: (id) => api.delete(`/kb/documents/${id}`),
  search: (q) => api.get(`/kb/search?q=${encodeURIComponent(q)}`),
};


export const aiChatAPI = {
  getTokenBalance: () => api.get('/ai-chat/token-balance'),
  listConversations: () => api.get('/ai-chat/conversations'),
  createConversation: (projectId = null) => api.post('/ai-chat/conversations', { project_id: projectId }),
  getMessages: (convId) => api.get(`/ai-chat/conversations/${convId}/messages`),
  sendMessage: (convId, formData) => api.post(`/ai-chat/conversations/${convId}/send`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  sendMessageStream: async (convId, formData, { onStep, onDone, onError }) => {
    const token = localStorage.getItem('stockara_token');
    let resp;
    try {
      resp = await fetch(`${API}/ai-chat/conversations/${convId}/send-stream`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
    } catch (e) {
      onError({ status: 0, detail: 'Koneksi terputus. Coba lagi ya.' });
      return;
    }
    if (!resp.ok || !resp.body) {
      let detail = 'Gagal mengirim pesan';
      try { const j = await resp.json(); detail = j.detail || detail; } catch (e) { /* ignore */ }
      onError({ status: resp.status, detail });
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = raw.split('\n').find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          let evt;
          try { evt = JSON.parse(dataLine.slice(6)); } catch (e) { continue; }
          if (evt.type === 'step') onStep(evt.label);
          else if (evt.type === 'done') onDone(evt);
          else if (evt.type === 'error') onError({ status: 200, detail: evt.detail });
        }
      }
    } catch (e) {
      onError({ status: 0, detail: 'Koneksi streaming terputus. Coba lagi ya.' });
    }
  },
  deleteConversation: (convId) => api.delete(`/ai-chat/conversations/${convId}`),
  renameConversation: (convId, title) => api.put(`/ai-chat/conversations/${convId}`, { title }),
  downloadFile: (fileUrl) => api.get(fileUrl.replace(/^\/api/, ''), { responseType: 'blob', timeout: 60000 }),
  truncateFrom: (convId, msgId) => api.delete(`/ai-chat/conversations/${convId}/messages/${msgId}`),
  exportConversation: (convId) => api.get(`/ai-chat/conversations/${convId}/export`, { responseType: 'blob', timeout: 30000 }),
  regenerateStream: async (convId, { onStep, onDone, onError }) => {
    const token = localStorage.getItem('stockara_token');
    let resp;
    try {
      resp = await fetch(`${API}/ai-chat/conversations/${convId}/regenerate`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (e) {
      onError({ status: 0, detail: 'Koneksi terputus. Coba lagi ya.' });
      return;
    }
    if (!resp.ok || !resp.body) {
      let detail = 'Gagal menyusun ulang jawaban';
      try { const j = await resp.json(); detail = j.detail || detail; } catch (e) { /* ignore */ }
      onError({ status: resp.status, detail });
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = raw.split('\n').find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          let evt;
          try { evt = JSON.parse(dataLine.slice(6)); } catch (e) { continue; }
          if (evt.type === 'step') onStep(evt.label);
          else if (evt.type === 'done') onDone(evt);
          else if (evt.type === 'error') onError({ status: 200, detail: evt.detail });
        }
      }
    } catch (e) {
      onError({ status: 0, detail: 'Koneksi streaming terputus. Coba lagi ya.' });
    }
  },
};

export const projectAPI = {
  list: () => api.get('/ai-chat/projects'),
  create: (name) => api.post('/ai-chat/projects', { name }),
  update: (id, data) => api.put(`/ai-chat/projects/${id}`, data),
  delete: (id) => api.delete(`/ai-chat/projects/${id}`),
  moveConversation: (convId, projectId) => api.put(`/ai-chat/conversations/${convId}/project`, { project_id: projectId }),
  listFiles: (id) => api.get(`/ai-chat/projects/${id}/files`),
  uploadFile: (id, formData) => api.post(`/ai-chat/projects/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  deleteFile: (id, fileId) => api.delete(`/ai-chat/projects/${id}/files/${fileId}`),
};

export const topupAPI = {
  getPackages: () => api.get('/topup/packages'),
  getStatus: () => api.get('/topup/status'),
  create: (packageId) => api.post('/topup/create', { package: packageId }),
  getHistory: () => api.get('/topup/history'),
};

export const billingAPI = {
  subscribe: (tier) => api.post('/billing/subscribe', { tier }),
  getPlans: () => api.get('/billing/plans'),
};

// Pembayaran versi Stockara: metode & instruksi dirender UI kita sendiri,
// gateway hanya dipakai di backend.
export const paymentAPI = {
  getMethods: ({ kind = 'subscription', tier = '', package: pkg = '' } = {}) =>
    api.get('/payment/methods', { params: { kind, tier, package: pkg } }),
  create: ({ kind = 'subscription', tier = '', package: pkg = '', method, phone = '' }) =>
    api.post('/payment/create', { kind, tier, package: pkg, method, phone }),
  getStatus: (orderId) => api.get(`/payment/status/${orderId}`),
};

export const scheduledTaskAPI = {
  list: () => api.get('/ai-chat/scheduled-tasks'),
  create: (data) => api.post('/ai-chat/scheduled-tasks', data),
  update: (id, data) => api.put(`/ai-chat/scheduled-tasks/${id}`, data),
  remove: (id) => api.delete(`/ai-chat/scheduled-tasks/${id}`),
  runNow: (id) => api.post(`/ai-chat/scheduled-tasks/${id}/run-now`, {}, { timeout: 60000 }),
  runs: (id) => api.get(`/ai-chat/scheduled-tasks/${id}/runs`),
};

export const dailyBriefingAPI = {
  status: () => api.get('/ai-chat/daily-briefing/status'),
  setup: (data) => api.post('/ai-chat/daily-briefing/setup', data),
  disable: () => api.post('/ai-chat/daily-briefing/disable', {}),
};

export const alertSettingsAPI = {
  get: () => api.get('/ai-chat/alert-settings'),
  update: (data) => api.put('/ai-chat/alert-settings', data),
  checkNow: () => api.post('/ai-chat/alert-settings/check-now', {}, { timeout: 30000 }),
};
